import { CompositionConfiguration, CompositionPreviewRow } from "../types";

import {
  getActiveSourceResolvers,
  getSourceTitleAtIndex,
  getSourceTypeAtIndex,
} from "./sourceConfiguration";

import { CompositionSourceGroup } from "@/queries/smart-tables/types";
import { parseSourceResolver } from "@/routes/smart-table/utils/sourceResolver";

interface ResolveSourcePreviewArgs {
  configuration: CompositionConfiguration;
  compositionGroups: CompositionSourceGroup[];
  currentTableId?: string;
  isLoadingSources: boolean;
}

const resolveSourcePreview = ({
  configuration,
  compositionGroups,
  currentTableId,
  isLoadingSources,
}: ResolveSourcePreviewArgs): CompositionPreviewRow[] | null => {
  const activeSources = getActiveSourceResolvers(configuration);
  if (activeSources.length === 0) {
    return null;
  }

  const shouldDeferStaleState = isLoadingSources && compositionGroups.length === 0;
  const pendingRemovedSources = new Set(configuration.pending_removed_sources ?? []);

  return activeSources.map((resolver, index) => {
    const parsed = parseSourceResolver(resolver);
    const isPendingRemoval = pendingRemovedSources.has(resolver);

    const fallbackColumnTitle = getSourceTitleAtIndex(configuration, index) ?? "this column";
    const fallbackTableTitle = configuration.smart_table_title ?? "Unknown table";
    const fallbackSheetTitle = configuration.sheet_title ?? "Unknown sheet";
    const fallbackColumnType = getSourceTypeAtIndex(configuration, index);

    if (!parsed) {
      return {
        resolver,
        tableTitle: fallbackTableTitle,
        sheetTitle: fallbackSheetTitle,
        columnTitle: fallbackColumnTitle,
        columnType: fallbackColumnType,
        isCurrentTable: false,
        isStale: true,
        isResolving: false,
        isPendingRemoval,
      };
    }

    const group = compositionGroups.find(
      g => g.smart_table_id === parsed.smartTableId && g.sheet_id === parsed.sheetId,
    );

    if (!group) {
      return {
        resolver,
        tableTitle: fallbackTableTitle,
        sheetTitle: fallbackSheetTitle,
        columnTitle: fallbackColumnTitle,
        columnType: fallbackColumnType,
        isCurrentTable: currentTableId === parsed.smartTableId,
        isStale: isPendingRemoval || !shouldDeferStaleState,
        isResolving: !isPendingRemoval && shouldDeferStaleState,
        isPendingRemoval,
      };
    }

    const column = group.columns.find(c => c.id === parsed.columnId);

    return {
      resolver,
      tableTitle: group.smart_table_title,
      sheetTitle: group.sheet_title,
      columnTitle: column?.title ?? fallbackColumnTitle ?? "Unknown column",
      columnType: (column?.type as CompositionPreviewRow["columnType"]) ?? fallbackColumnType,
      isCurrentTable: currentTableId === group.smart_table_id,
      isStale: isPendingRemoval || !column,
      isResolving: false,
      isPendingRemoval,
    };
  });
};

export { resolveSourcePreview };
export default resolveSourcePreview;
